import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Select,
  Typography,
  Table,
  Space,
  Input,
  Row,
  Col,
  Modal,
  Tooltip,
  Alert,
  List,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  LinkOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useGrades } from "../../../../hooks/useGrades";
import { useNotification } from "../../../../hooks/useNotification";
import { SchoolUserEntity, ClassEntity } from "../types";
import {
  ALL_CAMPUSES,
  buildUserFilters,
  campusOptions,
  canResendWelcomeEmail,
  didResendWelcomeSucceed,
  hasMultipleCampuses,
  saveUserErrorMessage,
  uniqueClassrooms,
} from "./UsersTab.utils";
import { CampusRef } from "../../../../services/schools/campus.types";
import MoveToCampusModal from "../../../MoveToCampusModal";
import {
  createSchoolUser,
  getSchoolUsersBySchoolId,
  updateSchoolUser,
  deleteSchoolUser,
  bulkCreateSchoolUsers,
  bulkValidateSchoolUsers,
  resendBulkTeacherInvitations,
} from "../../../../services/schools/school.service";
import {
  CreateSchoolUserDto,
  UpdateSchoolUserDto,
  BulkCreateUsersDto,
  BulkValidateUsersDto,
  DuplicatedEmail,
  SchoolUserPaginatedResponse,
} from "../../../../services/schools/school.types";
import ClassesSelector from "./ClassesSelector";
import BulkUploadModal from "./BulkUploadModal";
import {
  buildBulkOutcome,
  countUsers,
  UpdatedBulkUser,
} from "./BulkUploadModal.utils";
import ResendInvitationsModal from "./ResendInvitationsModal";
import DeleteUserConfirmModal from "../../../common/DeleteUserConfirmModal";

interface UsersTabProps {
  classes: ClassEntity[];
  schoolId: number | null;
  /**
   * SCRUM-916: árbol de planteles de la escuela. Vacío o de un solo elemento
   * en una escuela sin planteles, y entonces esta tab se ve igual que siempre.
   */
  campuses?: CampusRef[];
}

const { Option } = Select;
const { Title } = Typography;

const UsersTab: React.FC<UsersTabProps> = ({
  classes,
  schoolId,
  campuses = [],
}) => {
  const { grades } = useGrades();
  const { notify } = useNotification();

  // Estados para usuarios
  const [users, setUsers] = useState<SchoolUserEntity[]>([]);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingUser, setEditingUser] = useState<SchoolUserEntity | null>(null);
  const [loading, setLoading] = useState<boolean>(false); // loader para la tabla
  const [savingUser, setSavingUser] = useState<boolean>(false); // loader para guardar usuario
  const [resendingWelcomeUserId, setResendingWelcomeUserId] = useState<
    number | null
  >(null); // loader por fila para el reenvío del correo de bienvenida
  const [updatedUsersNotice, setUpdatedUsersNotice] = useState<
    UpdatedBulkUser[] | null
  >(null);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] =
    useState<boolean>(false); // estado para el modal de carga masiva
  const [resendInvitationsModalVisible, setResendInvitationsModalVisible] =
    useState<boolean>(false); // estado para el modal de reenvío de invitaciones

  // Estado para modal de confirmación de eliminación de usuario
  const [deleteConfirmUser, setDeleteConfirmUser] =
    useState<SchoolUserEntity | null>(null);

  // Estados para validación de email único
  const [emailConflictModalVisible, setEmailConflictModalVisible] =
    useState<boolean>(false);
  const [conflictingUser, setConflictingUser] =
    useState<DuplicatedEmail | null>(null);
  const [showEmailTooltip, setShowEmailTooltip] = useState<boolean>(false);

  // Estados para paginación
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  // Estados para filtros
  const [filterType, setFilterType] = useState<string>("all");
  const [filterGrade, setFilterGrade] = useState<number | undefined>(undefined);
  const [filterName, setFilterName] = useState<string>("");
  const [debouncedFilterName, setDebouncedFilterName] = useState<string>(""); // Nuevo estado para el valor debounced
  const [filterClassroom, setFilterClassroom] = useState<number | undefined>(
    undefined,
  );
  const [filterIsActive, setFilterIsActive] = useState<string>("all"); // Filtro para is_active
  const [filterCampus, setFilterCampus] = useState<number>(ALL_CAMPUSES);
  const showCampusFilter = hasMultipleCampuses(campuses);
  // Con un plantel elegido la columna sería redundante: todas las filas
  // pertenecen a ese plantel.
  const showCampusColumn = showCampusFilter && filterCampus === ALL_CAMPUSES;
  const [moveTargetUser, setMoveTargetUser] =
    useState<SchoolUserEntity | null>(null);

  // Debounce para el filtro de nombre
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterName(filterName);
    }, 500); // 500ms de espera

    return () => {
      clearTimeout(handler);
    };
  }, [filterName]);

  // Función para mapear usuario de API a entidad del componente
  const mapApiUserToComponent = useCallback(
    (apiUser: any): SchoolUserEntity => {
      // Manejar tanto classroom_ids (array de números) como classrooms (array de objetos)
      let classroomIds: number[] = [];

      if (apiUser.classroom_ids && Array.isArray(apiUser.classroom_ids)) {
        // Formato de listado: classroom_ids como array de números
        classroomIds = apiUser.classroom_ids;
      } else if (apiUser.classrooms && Array.isArray(apiUser.classrooms)) {
        // Formato de creación/actualización: classrooms como array de objetos
        classroomIds = apiUser.classrooms.map((classroom: any) => classroom.id);
      }

      const mappedUser: SchoolUserEntity = {
        id: apiUser.id,
        role: apiUser.role,
        grades: apiUser.grades || [], // Arreglo de grados
        first_name: apiUser.first_name || "",
        access_url: apiUser.access_url || "",
        last_name: apiUser.last_name || "",
        username: apiUser.username ?? null,
        email: apiUser.email || "",
        school_id: apiUser.school_id,
        campus: apiUser.campus,
        classroom_ids: classroomIds, // Array de IDs de clases
        is_active: apiUser.is_active !== undefined ? apiUser.is_active : true, // Estado activo/inactivo
      };

      return mappedUser;
    },
    [],
  );

  // Función específica para crear usuario
  const mapUserForCreate = useCallback(
    (user: SchoolUserEntity): CreateSchoolUserDto => {
      return {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        role: user.role,
        school_id: user.school_id!,
        grades: user.grades.length > 0 ? user.grades : undefined,
        classroom_ids: user.classroom_ids, // Siempre enviar el array, aunque esté vacío
      };
    },
    [],
  );

  // Función específica para actualizar usuario
  const mapUserForUpdate = useCallback(
    (user: SchoolUserEntity): UpdateSchoolUserDto => {
      return {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        role: user.role,
        school_id: user.school_id!,
        grades: user.grades.length > 0 ? user.grades : undefined,
        classroom_ids: user.classroom_ids,
        is_active: user.is_active,
      };
    },
    [],
  );

  // Función para validar que el array de clases no exceda 4 elementos SOLO para estudiantes
  const validateClassroomIds = useCallback(
    (user: SchoolUserEntity): boolean => {
      // Si es profesor o estudiante, no hay límite de clases
      if (user.role === "TEACHER" || user.role === "STUDENT") {
        return true;
      }

      // Si es coordinador, no debe tener clases asignadas
      if (user.role === "COORDINATOR") {
        return user.classroom_ids.length === 0;
      }

      return true;
    },
    [],
  );

  // Cargar usuarios al montar el componente y cuando cambian page/pageSize
  useEffect(() => {
    const loadUsers = async () => {
      if (!schoolId) return;

      setLoading(true);
      try {
        // Construir objeto de filtros
        const filters = buildUserFilters({
          role: filterType,
          isActive: filterIsActive,
          gradeId: filterGrade,
          classroomId: filterClassroom,
          search: debouncedFilterName,
          campusId: filterCampus,
        });

        // Llamada con filtros
        const resp = await getSchoolUsersBySchoolId(
          schoolId,
          page,
          pageSize,
          filters,
        );

        // Cambia aquí: resp.data y resp.total
        const mappedUsers = Array.isArray(resp.data)
          ? resp.data.map(mapApiUserToComponent)
          : [];
        setUsers(mappedUsers);
        setTotal(resp.total ?? 0);
      } catch (error) {
        console.error("Error loading school users:", error);
        notify("error", "Error", "Error al cargar los usuarios de la escuela.");
        setUsers([]);
        setTotal(0);
      }
      setLoading(false);
    };

    loadUsers();
  }, [
    schoolId,
    page,
    pageSize,
    filterType,
    filterGrade,
    debouncedFilterName,
    filterClassroom,
    filterIsActive,
    filterCampus,
    mapApiUserToComponent,
  ]);

  const reloadUsers = useCallback(async () => {
    if (!schoolId) return;

    //limpiar filtros
    setFilterType("all");
    setFilterGrade(undefined);
    setFilterClassroom(undefined);
    setFilterName("");
    setDebouncedFilterName("");
    setFilterIsActive("all");
    setFilterCampus(ALL_CAMPUSES);

    setLoading(true);
    try {
      const resp: SchoolUserPaginatedResponse = await getSchoolUsersBySchoolId(
        schoolId,
        1,
        pageSize,
        {},
      );
      const mappedUsers = Array.isArray(resp.data)
        ? resp.data.map(mapApiUserToComponent)
        : [];
      setUsers(mappedUsers);
      setTotal(resp.total ?? 0);
      setPage(1); // Reiniciar paginación
    } catch (error) {
      console.error("Error reloading school users:", error);
      notify("error", "Error", "Error al recargar los usuarios de la escuela.");
      setUsers([]);
      setTotal(0);
      setPage(1);
    }
    setLoading(false);
  }, [schoolId, pageSize, mapApiUserToComponent, notify]);

  const handleAddUser = () => {
    // Prevenir agregar nuevo usuario si hay un guardado en curso
    if (savingUser) {
      notify(
        "warning",
        "Advertencia",
        "Espera a que termine la operación actual antes de crear un nuevo usuario.",
      );
      return;
    }

    const newUser: SchoolUserEntity = {
      id: Date.now(), // ID temporal
      role: "STUDENT",
      grades: [], // Inicializar arreglo vacío de grados
      first_name: "",
      last_name: "",
      email: "",
      school_id: schoolId || undefined,
      classroom_ids: [], // Inicializar array vacío de clases
      is_active: true, // Por defecto activo
    };
    setUsers([newUser, ...users]);
    setEditingUserId(newUser.id);
    setEditingUser(newUser);
  };

  const handleEditUser = (userItem: SchoolUserEntity) => {
    // Prevenir edición si hay un guardado en curso
    if (savingUser) {
      notify(
        "warning",
        "Advertencia",
        "Espera a que termine la operación actual antes de editar otro usuario.",
      );
      return;
    }

    setEditingUserId(userItem.id);
    setEditingUser({ ...userItem });
  };

  const handleOpenUserPortal = (user: SchoolUserEntity) => {
    if (user.access_url) {
      window.open(user.access_url, "_blank", "noopener,noreferrer");
    } else {
      notify(
        "warning",
        "Advertencia",
        "No hay URL de acceso disponible para este usuario.",
      );
    }
  };

  // SCRUM-757: reuses the bulk resend endpoint with a single id so the email
  // is identical to the one sent when the user was created.
  const handleResendWelcomeEmail = async (user: SchoolUserEntity) => {
    if (!canResendWelcomeEmail(user)) {
      notify(
        "warning",
        "Advertencia",
        "Este usuario no tiene un correo registrado al que reenviar la bienvenida.",
      );
      return;
    }

    setResendingWelcomeUserId(user.id);
    try {
      const result = await resendBulkTeacherInvitations([user.id]);

      if (didResendWelcomeSucceed(result)) {
        notify("success", "Listo", "Correo de bienvenida reenviado.");
      } else {
        console.error("Resend welcome email returned no delivery:", result);
        notify(
          "error",
          "Error",
          "No se pudo reenviar el correo de bienvenida. Intenta nuevamente.",
        );
      }
    } catch (error) {
      console.error("Error resending welcome email:", error);
      notify(
        "error",
        "Error",
        "No se pudo reenviar el correo de bienvenida. Intenta nuevamente.",
      );
    } finally {
      setResendingWelcomeUserId(null);
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser || !schoolId) {
      notify(
        "error",
        "Error",
        "Faltan datos requeridos para guardar el usuario.",
      );
      return;
    }

    // Validar campos requeridos
    if (
      !editingUser.first_name.trim() ||
      !editingUser.last_name.trim() ||
      !editingUser.email.trim() ||
      editingUser.grades.length === 0
    ) {
      notify(
        "error",
        "Error",
        "Los campos Nombre, Apellido, Correo y al menos un Grado son obligatorios.",
      );
      return;
    }

    // Validar que los estudiantes solo tengan un grado
    if (editingUser.role === "STUDENT" && editingUser.grades.length > 1) {
      notify(
        "error",
        "Error",
        "Los estudiantes solo pueden tener un grado asignado.",
      );
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editingUser.email)) {
      notify(
        "error",
        "Error",
        "El formato del correo electrónico no es válido.",
      );
      return;
    }

    // Validar límite de clases
    if (!validateClassroomIds(editingUser)) {
      notify(
        "error",
        "Error",
        "Los coordinadores no pueden estar asignados a clases.",
      );
      return;
    }

    // Si es un nuevo usuario, validar que el email no exista
    if (editingUser.id > 1000000) {
      const emailExists = await validateUniqueEmail(editingUser.email);
      if (emailExists) {
        return; // El modal se maneja en validateUniqueEmail
      }
    }

    setSavingUser(true); // Activar loader

    try {
      if (editingUser.id > 1000000) {
        // Es un nuevo usuario (ID temporal)
        const userData = mapUserForCreate(editingUser);

        const savedUser = await createSchoolUser(userData);
        if (savedUser) {
          const mappedUser = mapApiUserToComponent(savedUser);
          const updatedUsers = users.map((u) =>
            u.id === editingUser.id ? mappedUser : u,
          );
          setUsers(updatedUsers);
          notify("success", "Éxito", "Usuario creado correctamente.");
        }
      } else {
        // Es un usuario existente
        const userData = mapUserForUpdate(editingUser);

        const updatedUser = await updateSchoolUser(editingUser.id, userData);
        if (updatedUser) {
          const mappedUser = mapApiUserToComponent(updatedUser);
          const updatedUsers = users.map((u) =>
            u.id === editingUser.id ? mappedUser : u,
          );
          setUsers(updatedUsers);
          notify("success", "Éxito", "Usuario actualizado correctamente.");
        }
      }

      setEditingUserId(null);
      setEditingUser(null);
    } catch (error: any) {
      console.error("Error saving user:", error);
      notify("error", "Error", saveUserErrorMessage(error));
    } finally {
      setSavingUser(false); // Desactivar loader
    }
  };
  const handleCancelEdit = () => {
    // Si es un usuario nuevo (ID temporal) y no tiene datos, eliminarlo de la lista
    if (editingUser && editingUser.id > 1000000) {
      const hasData =
        editingUser.first_name.trim() ||
        editingUser.last_name.trim() ||
        editingUser.email.trim() ||
        editingUser.grades.length > 0;

      if (!hasData) {
        const updatedUsers = users.filter((u) => u.id !== editingUser.id);
        setUsers(updatedUsers);
      }
    }

    setEditingUserId(null);
    setEditingUser(null);
  };

  const handleDeleteUser = async (id: number) => {
    try {
      // Si el ID es temporal (mayor a 1000000), solo remover de la lista local
      if (id > 1000000) {
        const updatedUsers = users.filter((u) => u.id !== id);
        setUsers(updatedUsers);
        notify("info", "Información", "Usuario removido de la lista.");
        setDeleteConfirmUser(null);
        return;
      }

      // Si es un usuario existente en el backend, eliminarlo
      await deleteSchoolUser(id);
      const updatedUsers = users.filter((u) => u.id !== id);
      setUsers(updatedUsers);
      notify("success", "Éxito", "Usuario eliminado correctamente.");
      setDeleteConfirmUser(null);
    } catch (error: any) {
      console.error("Error deleting user:", error);
      notify("error", "Error", "Ocurrió un error al eliminar el usuario.");
      setDeleteConfirmUser(null);
    }
  };

  // Función para manejar la carga masiva de usuarios
  const handleBulkUpload = async (data: BulkCreateUsersDto) => {
    if (!schoolId) {
      notify("error", "Error", "ID de escuela no válido.");
      return;
    }

    try {
      const result = await bulkCreateSchoolUsers(data);
      if (result) {
        const outcome = buildBulkOutcome(result);

        // The backend answers 201 with `requiresConfirmation` instead of
        // creating anything when it found duplicates and nobody opted into
        // updating them. Without this branch the modal used to close in
        // silence, as if the upload had worked.
        if (outcome.requiresConfirmation) {
          notify(
            "warning",
            "Confirmación requerida",
            "El archivo tiene correos que ya existen. Validá los usuarios y elegí si querés actualizarlos.",
          );
          return;
        }

        if (outcome.createdCount > 0 || outcome.updated.length > 0) {
          const parts: string[] = [];
          if (outcome.createdCount > 0) {
            parts.push(
              `${countUsers(outcome.createdCount)} ${
                outcome.createdCount === 1 ? "creado" : "creados"
              }`,
            );
          }
          if (outcome.updated.length > 0) {
            parts.push(
              `${outcome.updated.length} ${
                outcome.updated.length === 1 ? "actualizado" : "actualizados"
              }`,
            );
          }
          notify("success", "Éxito", `${parts.join(" y ")}.`);
          await reloadUsers();
        }

        // SCRUM-822: an update rewrote an existing user's grades and classes.
        // That is not visible anywhere else, so it gets its own notice.
        if (outcome.updated.length > 0) {
          setUpdatedUsersNotice(outcome.updated);
        }

        if (outcome.failedCount > 0) {
          notify(
            "warning",
            "Advertencia",
            `${countUsers(outcome.failedCount)} no ${
              outcome.failedCount === 1 ? "pudo" : "pudieron"
            } ser ${
              outcome.failedCount === 1 ? "procesado" : "procesados"
            }.`,
          );
        }

        if (
          outcome.createdCount === 0 &&
          outcome.updated.length === 0 &&
          outcome.failedCount === 0
        ) {
          notify(
            "warning",
            "Advertencia",
            "No se procesó ningún usuario del archivo.",
          );
        }
      } else {
        notify("warning", "Advertencia", "No se pudieron crear los usuarios.");
      }
    } catch (error: any) {
      console.error("Error in bulk upload:", error);
      notify(
        "error",
        "Error",
        "Ocurrió un error durante la carga masiva de usuarios.",
      );
    }
  };

  const handleOpenBulkUpload = () => {
    setBulkUploadModalVisible(true);
  };

  const handleCloseBulkUpload = () => {
    setBulkUploadModalVisible(false);
  };

  const handleOpenResendInvitations = () => {
    setResendInvitationsModalVisible(true);
  };

  const handleCloseResendInvitations = () => {
    setResendInvitationsModalVisible(false);
  };

  const handleUserFieldChange = (field: keyof SchoolUserEntity, value: any) => {
    if (editingUser) {
      let updatedUser = { ...editingUser, [field]: value };

      // Si se está cambiando el rol a STUDENT y tiene múltiples grados, mantener solo el primero
      if (
        field === "role" &&
        value === "STUDENT" &&
        updatedUser.grades &&
        updatedUser.grades.length > 1
      ) {
        updatedUser = { ...updatedUser, grades: [updatedUser.grades[0]] };
      }

      // Si se está cambiando el rol a COORDINATOR, limpiar solo las clases
      if (field === "role" && value === "COORDINATOR") {
        updatedUser = {
          ...updatedUser,
          classroom_ids: [],
        };
      }

      // Si se están cambiando los grados, limpiar clases que no correspondan
      if (field === "grades" && Array.isArray(value)) {
        // Filtrar las clases actuales para mantener solo las que corresponden a los nuevos grados
        const validClassroomIds = updatedUser.classroom_ids.filter(
          (classroomId) => {
            const classItem = classes.find((c) => c.id === classroomId);
            return classItem && value.includes(classItem.grade_id);
          },
        );

        updatedUser = { ...updatedUser, classroom_ids: validClassroomIds };
      }

      setEditingUser(updatedUser);
    }
  };

  // Obtener opciones de clases disponibles para el selector múltiple
  const getClassOptions = () => {
    if (!editingUser) {
      return [];
    }

    // Si el usuario tiene grados seleccionados, mostrar solo clases de esos grados
    const availableClasses = classes.filter((classItem) => {
      if (editingUser.grades && editingUser.grades.length > 0) {
        // Verificar que la clase pertenezca a uno de los grados seleccionados
        const belongs =
          classItem.grade_id !== undefined &&
          classItem.grade_id !== null &&
          editingUser.grades.includes(classItem.grade_id);

        return belongs;
      }
      return true;
    });

    const options = uniqueClassrooms(availableClasses).map((classItem) => {
      const grade = grades.find((g) => g.id === classItem.grade_id);
      const option = {
        value: classItem.id,
        label: `${
          classItem.class_code || classItem.custom_code || "Código pendiente"
        }${grade ? ` (${grade.code})` : ""}`,
      };
      return option;
    });

    return options || [];
  };

  // Función para manejar el cambio de clases seleccionadas
  const handleClassesChange = (selectedClassIds: number[]) => {
    if (!editingUser) return;

    // Asegurar que siempre sea un array (Ant Design a veces puede pasar undefined)
    const classIds = selectedClassIds || [];

    // Validar límite para coordinadores
    if (editingUser.role === "COORDINATOR" && classIds.length > 0) {
      notify(
        "warning",
        "Advertencia",
        "Los coordinadores no pueden estar asignados a clases.",
      );
      return;
    }

    // Actualizar directamente el array de clases
    const updatedUser = { ...editingUser, classroom_ids: classIds };

    setEditingUser(updatedUser);
  };

  // Función para obtener las clases actualmente seleccionadas
  const getSelectedClasses = () => {
    if (!editingUser) return [];

    const classIds = editingUser.classroom_ids || [];

    // Asegurar que sea un array válido y que todos los elementos sean números
    const validClassIds = Array.isArray(classIds)
      ? classIds.filter((id) => typeof id === "number" && !isNaN(id))
      : [];

    return validClassIds;
  };

  // Definir las columnas para la tabla de usuarios
  const userColumns = [
    // SCRUM-916 (R3.11): la columna sólo aparece cuando hay más de un plantel
    // que distinguir. Con un plantel sería una columna con el mismo valor en
    // todas las filas.
    ...(showCampusColumn
      ? [
          {
            title: "Plantel",
            dataIndex: "campus",
            key: "campus",
            width: 140,
            render: (campus: SchoolUserEntity["campus"]) =>
              campus?.name ?? "—",
          },
        ]
      : []),
    {
      title: "Tipo",
      dataIndex: "role",
      key: "role",
      width: 100,
      sorter: (a: SchoolUserEntity, b: SchoolUserEntity) => {
        const roleOrder = { COORDINATOR: 0, TEACHER: 1, STUDENT: 2 };
        return (
          roleOrder[a.role as keyof typeof roleOrder] -
          roleOrder[b.role as keyof typeof roleOrder]
        );
      },
      render: (role: string, record: SchoolUserEntity) => {
        if (editingUserId === record.id) {
          return (
            <Select
              value={editingUser?.role || "STUDENT"}
              onChange={(value) => handleUserFieldChange("role", value)}
              style={{ width: "100%" }}
            >
              <Option value="STUDENT">Estudiante</Option>
              <Option value="TEACHER">Profesor</Option>
              <Option value="COORDINATOR">Coordinador</Option>
            </Select>
          );
        }
        const roleText =
          role === "TEACHER"
            ? "Profesor"
            : role === "COORDINATOR"
              ? "Coordinador"
              : "Estudiante";
        return <strong>{roleText}</strong>;
      },
    },
    {
      title: "Activo",
      dataIndex: "is_active",
      key: "is_active",
      width: 90,
      align: "center" as const,
      sorter: (a: SchoolUserEntity, b: SchoolUserEntity) => {
        return a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1;
      },
      render: (isActive: boolean, record: SchoolUserEntity) => {
        if (editingUserId === record.id) {
          return (
            <input
              type="checkbox"
              checked={editingUser?.is_active || false}
              onChange={(e) =>
                handleUserFieldChange("is_active", e.target.checked)
              }
              style={{ cursor: "pointer", width: "16px", height: "16px" }}
            />
          );
        }
        return isActive ? (
          <CheckOutlined style={{ color: "#52c41a", fontSize: "16px" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
        );
      },
    },
    {
      title: "Grados",
      dataIndex: "grades",
      key: "grades",
      width: 140,
      sorter: (a: SchoolUserEntity, b: SchoolUserEntity) => {
        const aGrades = a.grades || [];
        const bGrades = b.grades || [];
        if (aGrades.length === 0 && bGrades.length === 0) return 0;
        if (aGrades.length === 0) return 1;
        if (bGrades.length === 0) return -1;
        return aGrades[0] - bGrades[0]; // Ordenar por el primer grado
      },
      render: (gradeIds: number[], record: SchoolUserEntity) => {
        if (editingUserId === record.id) {
          const isStudent = editingUser?.role === "STUDENT";

          return (
            <Select
              mode={isStudent ? undefined : "multiple"}
              value={
                isStudent
                  ? editingUser?.grades?.[0] || undefined
                  : editingUser?.grades || []
              }
              onChange={(value) => {
                const gradesValue = isStudent ? (value ? [value] : []) : value;

                // Agregar un pequeño delay para asegurar que el estado se actualice
                setTimeout(() => {
                  handleUserFieldChange("grades", gradesValue);
                }, 10);
              }}
              placeholder={
                isStudent ? "Seleccionar grado" : "Seleccionar grados"
              }
              style={{ width: "100%" }}
              maxTagCount={isStudent ? undefined : 1}
              maxTagTextLength={8}
            >
              {grades.map((grade) => (
                <Option key={grade.id} value={grade.id}>
                  {grade.code}
                </Option>
              ))}
            </Select>
          );
        }

        // Mostrar grados asignados
        const allGrades = new Set();
        if (record.grades) record.grades.forEach((id) => allGrades.add(id));

        if (allGrades.size === 0) return "Sin asignar";

        const gradeNames = Array.from(allGrades).map((gradeId) => {
          const grade = grades.find((g) => g.id === gradeId);
          return grade ? grade.code : `ID: ${gradeId}`;
        });

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {gradeNames.map((gradeName, index) => (
              <span key={index} style={{ fontSize: "12px", lineHeight: "1.2" }}>
                {gradeName}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Nombre",
      dataIndex: "first_name",
      key: "first_name",
      width: 200,
      sorter: (a: SchoolUserEntity, b: SchoolUserEntity) =>
        a.first_name.localeCompare(b.first_name),
      render: (firstName: string, record: SchoolUserEntity) => {
        if (editingUserId === record.id) {
          return (
            <Input
              value={editingUser?.first_name || ""}
              onChange={(e) =>
                handleUserFieldChange("first_name", e.target.value)
              }
              placeholder="Nombre"
            />
          );
        }
        return firstName;
      },
    },
    {
      title: "Apellido",
      dataIndex: "last_name",
      key: "last_name",
      width: 180,
      sorter: (a: SchoolUserEntity, b: SchoolUserEntity) =>
        a.last_name.localeCompare(b.last_name),
      render: (lastName: string, record: SchoolUserEntity) => {
        if (editingUserId === record.id) {
          return (
            <Input
              value={editingUser?.last_name || ""}
              onChange={(e) =>
                handleUserFieldChange("last_name", e.target.value)
              }
              placeholder="Apellido"
            />
          );
        }
        return lastName;
      },
    },
    {
      title: "Correo",
      dataIndex: "email",
      key: "email",
      width: 250,
      sorter: (a: SchoolUserEntity, b: SchoolUserEntity) =>
        a.email.localeCompare(b.email),
      render: (email: string, record: SchoolUserEntity) => {
        if (editingUserId === record.id) {
          return (
            <Tooltip
              title="Este email ya existe en el sistema"
              open={showEmailTooltip && editingUser?.email === email}
              color="orange"
            >
              <Input
                value={editingUser?.email || ""}
                onChange={(e) => handleUserFieldChange("email", e.target.value)}
                placeholder="correo@ejemplo.com"
                type="email"
              />
            </Tooltip>
          );
        }
        return email;
      },
    },
    {
      title: "Usuario",
      dataIndex: "username",
      key: "username",
      width: 180,
    },
    {
      title: "Clases",
      dataIndex: "classroom_ids",
      key: "classroom_ids",
      width: 300,
      render: (_: any, record: SchoolUserEntity) => {
        if (editingUserId === record.id) {
          const userGrades = editingUser?.grades || [];
          const isCoordinator = editingUser?.role === "COORDINATOR";

          if (isCoordinator) {
            return (
              <Select
                mode="multiple"
                value={[]}
                placeholder="Los coordinadores no tienen clases asignadas"
                style={{ width: "100%" }}
                disabled={true}
              />
            );
          }

          return (
            <div
              key={`classes-wrapper-${editingUser?.id}-${userGrades.join("-")}`}
            >
              <ClassesSelector
                userGrades={userGrades}
                selectedClassIds={getSelectedClasses()}
                options={getClassOptions()}
                onChange={handleClassesChange}
              />
            </div>
          );
        }

        // Mostrar clases asignadas como tags
        const assignedClasses = record.classroom_ids || [];

        if (assignedClasses.length === 0) {
          return <span style={{ color: "#999" }}>Sin asignar</span>;
        }

        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {assignedClasses.map((classId) => {
              const classItem = classes.find((c) => c.id === classId);
              const grade = grades.find((g) => g.id === classItem?.grade_id);

              if (!classItem) {
                return (
                  <span
                    key={classId}
                    style={{
                      background: "var(--bo-inset-strong-bg)",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      color: "#999",
                    }}
                  >
                    Clase no encontrada
                  </span>
                );
              }

              return (
                <span
                  key={classId}
                  style={{
                    background: "#e6f7ff",
                    border: "1px solid #91d5ff",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    color: "#1890ff",
                    fontWeight: 500,
                  }}
                >
                  {classItem.class_code ||
                    classItem.custom_code ||
                    "Sin código"}
                  {grade && (
                    // Fixed gray: this sits on the chip's theme-invariant
                    // light-blue background, not on the themed surface.
                    <span style={{ color: "#666", fontWeight: 400 }}>
                      {" "}
                      ({grade.code})
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      width: 380,
      fixed: "right" as const,
      render: (_: any, record: SchoolUserEntity) => {
        if (editingUserId === record.id) {
          return (
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveUser}
                size="small"
                loading={savingUser}
                disabled={savingUser}
              >
                {savingUser ? "Guardando..." : "Guardar"}
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancelEdit}
                size="small"
                disabled={savingUser}
              >
                Cancelar
              </Button>
            </Space>
          );
        }
        return (
          <Space>
            {/* Botón de acceso al portal solo para usuarios NO estudiantes */}
            {record.role !== "STUDENT" && record.access_url && (
              <Tooltip title="Abrir web con el usuario">
                <Button
                  type="default"
                  icon={<LinkOutlined />}
                  onClick={() => handleOpenUserPortal(record)}
                  size="small"
                  disabled={savingUser}
                  style={{
                    backgroundColor: "#52c41a",
                    borderColor: "#52c41a",
                    color: "white",
                  }}
                >
                  Abrir
                </Button>
              </Tooltip>
            )}
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
              size="small"
              disabled={savingUser}
            >
              Editar
            </Button>
            {canResendWelcomeEmail(record) && (
              <Tooltip title="Reenviar el correo de bienvenida a este usuario">
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={() => handleResendWelcomeEmail(record)}
                  size="small"
                  loading={resendingWelcomeUserId === record.id}
                  disabled={savingUser || resendingWelcomeUserId !== null}
                >
                  Resetear
                </Button>
              </Tooltip>
            )}
            {showCampusFilter && (
              <Button
                type="default"
                icon={<SwapOutlined />}
                onClick={() => setMoveTargetUser(record)}
                size="small"
                disabled={savingUser}
              >
                Mover a plantel
              </Button>
            )}
            <Button
              type="default"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteConfirmUser(record)}
              size="small"
              disabled={savingUser}
            >
              Eliminar
            </Button>
          </Space>
        );
      },
    },
  ];

  // Función para validar email único
  const validateUniqueEmail = async (email: string): Promise<boolean> => {
    if (!schoolId || !editingUser) return false;

    try {
      const validationData: BulkValidateUsersDto = {
        school_id: schoolId,
        users: [
          {
            first_name: editingUser.first_name,
            last_name: editingUser.last_name,
            email: email,
            role: editingUser.role,
            grades: editingUser.grades,
          },
        ],
      };

      const validationResult = await bulkValidateSchoolUsers(validationData);

      if (
        validationResult?.hasConflicts &&
        validationResult.duplicatedEmails.length > 0
      ) {
        const conflict = validationResult.duplicatedEmails[0];
        setConflictingUser(conflict);

        // Mostrar tooltip
        setShowEmailTooltip(true);
        setTimeout(() => setShowEmailTooltip(false), 5000);

        // Mostrar modal
        setEmailConflictModalVisible(true);

        return true; // Hay conflicto
      }

      return false; // No hay conflicto
    } catch (error) {
      console.error("Error validating unique email:", error);
      notify("error", "Error", "Error al validar el email.");
      return false;
    }
  };

  // Función para manejar la suspensión (cancelar creación)
  const handleSuspendCreation = () => {
    setEmailConflictModalVisible(false);
    setConflictingUser(null);
    // No hacer nada más, mantener al usuario en modo edición
  };

  // Función para actualizar la información del usuario existente
  const handleUpdateExistingUser = async () => {
    if (!conflictingUser || !editingUser || !schoolId) return;

    setSavingUser(true); // Activar loader

    try {
      // Actualizar el usuario existente con la nueva información
      const updateData = mapUserForUpdate({
        ...editingUser,
        id: conflictingUser.existingUser.id, // Usar el ID del usuario existente
      });

      const updatedUser = await updateSchoolUser(
        conflictingUser.existingUser.id,
        updateData,
      );
      if (updatedUser) {
        const mappedUser = mapApiUserToComponent(updatedUser);

        // Remover el usuario temporal y agregar/actualizar el usuario existente
        const updatedUsers = users.filter((u) => u.id !== editingUser.id);
        const existingUserIndex = updatedUsers.findIndex(
          (u) => u.id === conflictingUser.existingUser.id,
        );

        if (existingUserIndex !== -1) {
          updatedUsers[existingUserIndex] = mappedUser;
        } else {
          updatedUsers.unshift(mappedUser); // Agregar al inicio si no existe en la lista actual
        }

        setUsers(updatedUsers);
        notify("success", "Éxito", "Usuario actualizado correctamente.");
      }

      setEditingUserId(null);
      setEditingUser(null);
      setEmailConflictModalVisible(false);
      setConflictingUser(null);
    } catch (error: any) {
      console.error("Error updating existing user:", error);
      notify("error", "Error", "Error al actualizar el usuario existente.");
    } finally {
      setSavingUser(false); // Desactivar loader
    }
  };

  return (
    <div className="users-container">
      {/* Header con título y botón agregar */}
      <div className="users-header">
        <Space>
          <Title level={4}>Gestión de Usuarios</Title>
          <Space size="small">
            <Button
              type="default"
              icon={<MailOutlined />}
              onClick={handleOpenResendInvitations}
              style={{
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
                color: "white",
              }}
              className="resend-invitations-btn"
            >
              Enviar Mails
            </Button>
            <Tooltip
              title="Reenvía invitaciones por correo a profesores que aún no han creado su contraseña. Útil cuando los emails iniciales no llegaron o se perdieron, o cuando se modifica el mail y necesitas enviarle el correo nuevamente."
              placement="top"
              overlayStyle={{ maxWidth: 300 }}
            >
              <QuestionCircleOutlined
                style={{
                  color: "#1890ff",
                  fontSize: "16px",
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Space>
        </Space>
        <Space>
          <Button
            type="default"
            onClick={reloadUsers}
            className="reload-users-btn"
            disabled={savingUser}
          >
            Recargar Usuarios
          </Button>
          <Button
            type="default"
            icon={<UploadOutlined />}
            onClick={handleOpenBulkUpload}
            style={{
              backgroundColor: "#52c41a",
              borderColor: "#52c41a",
              color: "white",
            }}
            className="bulk-upload-btn"
            disabled={savingUser}
          >
            Subir Bulk
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddUser}
            className="add-user-btn"
            disabled={savingUser}
          >
            Nuevo Usuario
          </Button>
        </Space>
      </div>

      {/* Filtros */}
      <div className="users-filters">
        <Row gutter={16}>
          {/* SCRUM-916: con el plantel los spans pasan de 5+4+5+5+5 a seis
              columnas de 4; sin planteles la fila queda como estaba. */}
          {showCampusFilter && (
            <Col span={4}>
              <Select
                value={filterCampus}
                onChange={(value: number) => {
                  setFilterCampus(value);
                  setPage(1);
                }}
                aria-label="Plantel"
                style={{ width: "100%" }}
                options={campusOptions(campuses)}
              />
            </Col>
          )}
          <Col span={showCampusFilter ? 4 : 5}>
            <Select
              value={filterType}
              onChange={setFilterType}
              placeholder="Filtrar por tipo"
              style={{ width: "100%" }}
            >
              <Option value="all">Todos los tipos</Option>
              <Option value="STUDENT">Estudiantes</Option>
              <Option value="TEACHER">Profesores</Option>
              <Option value="COORDINATOR">Coordinadores</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={filterIsActive}
              onChange={setFilterIsActive}
              placeholder="Filtrar por estado"
              style={{ width: "100%" }}
            >
              <Option value="all">Todos</Option>
              <Option value="true">Activos</Option>
              <Option value="false">Inactivos</Option>
            </Select>
          </Col>
          <Col span={showCampusFilter ? 4 : 5}>
            <Select
              value={filterGrade}
              onChange={setFilterGrade}
              placeholder="Filtrar por grado"
              style={{ width: "100%" }}
              allowClear
            >
              {grades.map((grade) => (
                <Option key={grade.id} value={grade.id}>
                  {grade.grade}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={showCampusFilter ? 4 : 5}>
            <Select
              value={filterClassroom}
              onChange={setFilterClassroom}
              placeholder="Filtrar por clase"
              style={{ width: "100%" }}
              allowClear
            >
              {uniqueClassrooms(classes).map((classItem) => {
                const grade = grades.find((g) => g.id === classItem.grade_id);
                return (
                  <Option key={classItem.id} value={classItem.id}>
                    {`${grade ? grade.grade : "Sin grado"} - ${
                      classItem.class_code ||
                      classItem.custom_code ||
                      "Código pendiente"
                    }`}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col span={showCampusFilter ? 4 : 5}>
            <Input
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Buscar por nombre, apellido, usuario o correo"
              allowClear
            />
          </Col>
        </Row>
      </div>

      {/* Tabla de usuarios */}
      <Table
        columns={userColumns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} usuarios`,
          pageSizeOptions: ["10", "20", "30"],
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize || 10);
          },
        }}
        scroll={{ x: 1560 }}
        className="users-table"
        locale={{
          emptyText:
            "No hay usuarios registrados. Haz clic en 'Nuevo Usuario' para comenzar.",
        }}
      />

      {/* SCRUM-916: mover a un usuario a otro plantel del árbol */}
      {moveTargetUser && schoolId && (
        <MoveToCampusModal
          open
          onClose={() => setMoveTargetUser(null)}
          rootSchoolId={schoolId}
          currentSchoolId={moveTargetUser.campus?.id ?? moveTargetUser.school_id ?? schoolId}
          target={{
            kind: "user",
            id: moveTargetUser.id,
            label: `${moveTargetUser.first_name} ${moveTargetUser.last_name}`.trim(),
          }}
          onMoved={() => {
            setMoveTargetUser(null);
            reloadUsers();
          }}
        />
      )}

      {/* Modal de carga masiva */}
      <BulkUploadModal
        visible={bulkUploadModalVisible}
        onClose={handleCloseBulkUpload}
        onUpload={handleBulkUpload}
        schoolId={schoolId || 0}
        classes={classes}
        campuses={campuses}
      />

      {/* SCRUM-822: aviso general de los usuarios que ya existían y se actualizaron */}
      <Modal
        title={
          (updatedUsersNotice?.length ?? 0) === 1
            ? "Usuario actualizado"
            : "Usuarios actualizados"
        }
        open={updatedUsersNotice !== null}
        onCancel={() => setUpdatedUsersNotice(null)}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setUpdatedUsersNotice(null)}
          >
            Entendido
          </Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          message={`${countUsers(updatedUsersNotice?.length ?? 0)} ya ${
            (updatedUsersNotice?.length ?? 0) === 1 ? "existía" : "existían"
          } con ese correo`}
          description={`En lugar de ${
            (updatedUsersNotice?.length ?? 0) === 1 ? "crearlo" : "crearlos"
          } se actualizaron sus datos con lo que traía el archivo (grado, grupo y clases).`}
          style={{ marginBottom: 16 }}
        />
        <List
          size="small"
          dataSource={updatedUsersNotice ?? []}
          renderItem={(user) => (
            <List.Item key={user.id}>
              <List.Item.Meta title={user.name} description={user.email} />
            </List.Item>
          )}
        />
      </Modal>

      {/* Modal de reenvío de invitaciones */}
      <ResendInvitationsModal
        visible={resendInvitationsModalVisible}
        onClose={handleCloseResendInvitations}
        schoolId={schoolId}
      />

      {/* Modal de confirmación de eliminación de usuario */}
      <DeleteUserConfirmModal
        visible={deleteConfirmUser !== null}
        userName={
          deleteConfirmUser
            ? `${deleteConfirmUser.first_name} ${deleteConfirmUser.last_name}`
            : ""
        }
        onConfirm={() =>
          deleteConfirmUser && handleDeleteUser(deleteConfirmUser.id)
        }
        onCancel={() => setDeleteConfirmUser(null)}
      />

      {/* Modal de conflicto de email */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: "#faad14" }} />
            Email duplicado
          </Space>
        }
        open={emailConflictModalVisible}
        onCancel={handleSuspendCreation}
        footer={[
          <Button
            key="suspend"
            onClick={handleSuspendCreation}
            disabled={savingUser}
          >
            Suspender
          </Button>,
          <Button
            key="update"
            type="primary"
            onClick={handleUpdateExistingUser}
            loading={savingUser}
            disabled={savingUser}
          >
            {savingUser ? "Actualizando..." : "Actualizar información"}
          </Button>,
        ]}
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <p>
            El email <strong>{conflictingUser?.email}</strong> ya existe en el
            sistema.
          </p>

          {conflictingUser && (
            <div style={{ marginTop: 16 }}>
              <h4>Usuario existente:</h4>
              <p>
                <strong>Nombre:</strong>{" "}
                {conflictingUser.existingUser.first_name}{" "}
                {conflictingUser.existingUser.last_name}
              </p>
              <p>
                <strong>Email:</strong> {conflictingUser.existingUser.email}
              </p>
              <p>
                <strong>Rol:</strong>{" "}
                {conflictingUser.existingUser.role === "TEACHER"
                  ? "Profesor"
                  : conflictingUser.existingUser.role === "COORDINATOR"
                    ? "Coordinador"
                    : "Estudiante"}
              </p>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <p>¿Qué deseas hacer?</p>
            <ul>
              <li>
                <strong>Suspender:</strong> Cancelar la creación y mantener el
                usuario existente sin cambios.
              </li>
              <li>
                <strong>Actualizar información:</strong> Actualizar los datos
                del usuario existente con la información ingresada.
              </li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsersTab;
